import Button from '2-core-elements/Buttons/Button';
import Icon from '2-core-elements/Icon/Icon';
import Chip from '3-components/Chip/Chip';
import clsx from 'clsx';
import { type FC } from 'react';
import { pushGTMEvent } from 'utils/eventGTM';

export interface EventCalendarCardProps {
    url: string;
    title: string;
    category?: string;
    tagLabel: string;
    cardType: string;
    type: string;
    cardIcon: string;
    date: string | number;
    description?: string;
    secondaryDescription?: string;
    team1?: string;
    team2?: string;
    calendarTags?: string[];
    status?: string;
}

const EventCalendarCard: FC<EventCalendarCardProps> = ({
    url,
    title,
    type,
    date,
    description,
    secondaryDescription,
    status = 'none',
    cardType,
    tagLabel,
    cardIcon,
    // team1, team2, category, calendarTags are used by parent components for filtering
    ..._ // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
    const iconTypes: { [key: string]: string } = {
        afl: 'afl',
        dining: 'fork-knife',
        'club functions': 'club-function',
        concerts: 'rock-n-roll',
        cricket: 'cricket',
        experiences: 'stadium',
        tours: 'stadium',
        asm: 'asm-experience',
        'special event': 'star',
    };

    const iconType = iconTypes[cardIcon.toLowerCase()];

    const isRegularEvent = ['afl', 'concerts', 'cricket'].includes(type.toLowerCase());
    const itemStatus = status?.toLowerCase().replace(/\s+/g, '-') || 'none';
    const isDetailNotAvailable = itemStatus === 'closed' || itemStatus === 'tbc';

    // Extract the time from the date string
    const eventDate = new Date(date);
    const eventTime = eventDate.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'Australia/Sydney',
    }).replace(/\s([ap]m)$/, '$1'); // Removes any whitespace before AM/PM

    const cardClassName = clsx(
        'event-calendar-card',
        cardType ? `is-${cardType}-card` : null,
        cardIcon.toLowerCase() === 'asm' ? 'asm-experience' : (cardIcon ?? '').toLowerCase().replace(/\s+/g, '-'),
        itemStatus ? `is-status-${itemStatus}` : null,
        isRegularEvent ? 'is-regular-event' : ''
    );

    return (
        <li>
            <a
                href={!isDetailNotAvailable ? url : undefined}
                className={cardClassName}
                aria-label={`${title} ${description || eventTime} ${secondaryDescription ? secondaryDescription : ''}`}
                onClick={(): void => {
                    pushGTMEvent({
                        actionType: 'calendar_event',
                        ev_name: title,
                        ev_type: type.toLowerCase(),
                        ev_date: eventDate.toLocaleDateString('en-AU'),
                        link_url: url,
                    });
                }}
            >
                <div className="event-calendar-card-type">
                    <Icon icon={iconType} />
                </div>
                <div className="event-calendar-card-content">
                    {tagLabel && (
                        <div className="event-calendar-card-tag">
                            <Chip
                                label={tagLabel}
                                variant="primary"
                                size="sm"
                            />
                        </div>
                    )}
                    <div className="event-calendar-card-title">{title}</div>
                    <div className="event-calendar-card-details">
                        {description || eventTime}
                    </div>
                    {secondaryDescription && (
                        <div className="event-calendar-card-secondary">
                            {secondaryDescription}
                        </div>
                    )}
                </div>
                {!isDetailNotAvailable && (
                    <Button skin="2" size="sm" asDiv={true} suffixIcon="arrow-right" className="event-calendar-card-view">
                        View
                    </Button>
                )}
            </a>
        </li>
    );
};

export default EventCalendarCard;
